import {AFTX_MENU_ENDPOINT, REGION, ENTERPRISE_SCOPE} from "src/common/constants";
import { TFunction } from "i18next";
import { ValidationError } from "src/models/validation-error";
import { logger } from "src/logger";
import { TenantID, AuthgardAdminErrorCode, AuthgardMfaStatus, IdpType, MfaProvider, UserType } from "src/common/enums";

/**
 * Converts the {@link Date} object to the local date {@link String} format.
 *
 * @param locale the locale (e.g. en-US).
 * @param date the date to convert.
 * @return the formatted date as a {@link String}.
 */
export const convertDateToLocaleFormat = (locale: string, date?: Date): string => {
    if (!date) {
        return '';
    }

    return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
}

/**
 * Converts the {@link Date} object to the local date and time {@link String} format.
 *
 * @param locale the locale (e.g. en-US).
 * @param date the date to convert.
 * @return the formatted date as a {@link String}.
 */
export const convertDateTimeToLocaleFormat = (locale: string, date?: Date): string => {
    if (!date) {
        return '';
    }

    return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
}

/**
 * Converts the {@link Date} object to the locale UTC 24-hour date/time {@link String} format.
 *
 * @param locale the locale (e.g. en-US).
 * @param date the date to convert.
 * @return the formatted date as a {@link String}.
 */
export const convertDateTimeToLocaleUTCFormat = (locale: string, date: Date): string => {
    return date.toLocaleString(locale, {
        timeZone: "UTC",
        timeZoneName: "short",
        // @ts-ignore
        hourCycle: "h24"
    });
}

/**
 * Returns a human-friendly invite-expiry label combining relative ("Expires in 8 hours")
 * and absolute timestamps ("at 29 Apr 2026, 3:42 PM IST"). Past-due dates render as
 * "Expired N ago (at …)". Both the relative phrasing (localized, ICU-pluralized) and the
 * absolute timestamp resolve to the caller's locale.
 *
 * @param t the i18n translation function.
 * @param date the expiry timestamp.
 * @param now optional reference point (for tests). Defaults to {@link Date.now}.
 * @return label string, or the em-dash placeholder if date is null/undefined.
 */
export const formatExpiryLabel = (t: TFunction, date?: Date | null, now: Date = new Date()): string => {
    if (!date) {
        return t('mfa-expiry-none');
    }

    const diffMs = date.getTime() - now.getTime();
    const past = diffMs < 0;
    const absMs = Math.abs(diffMs);

    const minute = 60_000;
    const hour = 60 * minute;
    const day = 24 * hour;

    // Pick the unit from the ROUNDED count so a value that rounds up to a unit boundary promotes
    // to the next unit ("1 hour", not "60 minutes"; "1 day", not "24 hours"). Selecting the bucket
    // from the raw value and rounding inside it let e.g. 59.7 minutes render as "60 minutes".
    const minutes = Math.round(absMs / minute);
    const hours = Math.round(absMs / hour);
    let relative: string;
    if (absMs < minute) {
        relative = past ? t('mfa-expiry-past-just-now') : t('mfa-expiry-future-lt-minute');
    } else if (minutes < 60) {
        relative = past ? t('mfa-expiry-past-minutes', { count: minutes }) : t('mfa-expiry-future-minutes', { count: minutes });
    } else if (hours < 24) {
        relative = past ? t('mfa-expiry-past-hours', { count: hours }) : t('mfa-expiry-future-hours', { count: hours });
    } else {
        const count = Math.round(absMs / day);
        relative = past ? t('mfa-expiry-past-days', { count }) : t('mfa-expiry-future-days', { count });
    }

    const absolute = date.toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    });

    return t('mfa-expiry-with-absolute', { relative, absolute });
};

/**
 * Plain-language, localized copy for each MFA status. Keeps the raw enum out of the UI so admins
 * see "Enrollment in progress" instead of "REINITIATED". INITIATED and REINITIATED collapse to one
 * user-facing status, as do RESET_FIDO2 and REREGISTER (same affordances).
 *
 * @param t the i18n translation function.
 * @param status the MFA status to describe.
 * @param isExpired when true, appends the "(invite expired)" qualifier.
 */
export const humanizeMfaStatus = (t: TFunction, status: AuthgardMfaStatus, isExpired = false): string => {
    const label = (() => {
        switch (status) {
            case AuthgardMfaStatus.NO_RECORD: return t('mfa-status-not-enrolled');
            case AuthgardMfaStatus.INITIATED:
            case AuthgardMfaStatus.REINITIATED: return t('mfa-status-in-progress');
            case AuthgardMfaStatus.ONBOARDED: return t('mfa-status-enrolled');
            case AuthgardMfaStatus.RESET_FIDO2:
            case AuthgardMfaStatus.REREGISTER: return t('mfa-status-revoked');
            case AuthgardMfaStatus.UNKNOWN: return t('mfa-status-unavailable');
            default: return t('mfa-status-unknown');
        }
    })();
    return isExpired ? t('mfa-status-invite-expired', { label }) : label;
};

/**
 * Localized, plain-language copy for the admin-operation / MFA-status error codes returned by the
 * backend (AdminOperationFailure.errorCode). Mirrors the smithy enum AdminOperationErrorCode
 * in FITPrincipalControlServiceModel. Unknown codes fall back to a generic message plus the
 * raw code in parentheses, so a newly added backend code is still legible (and greppable)
 * rather than shown as an opaque token.
 *
 * @param t the i18n translation function.
 * @param code the backend error code, if any.
 */
export const humanizeAdminErrorCode = (t: TFunction, code?: string): string => {
    switch (code) {
        case AuthgardAdminErrorCode.INVALID_USER_ID:
            return t('mfa-error-invalid-user-id');
        case AuthgardAdminErrorCode.INVALID_LOGIN:
            return t('mfa-error-invalid-login');
        case AuthgardAdminErrorCode.SERVER_ERROR:
            return t('mfa-error-server-error');
        case AuthgardAdminErrorCode.USER_NOT_INITIATED:
            return t('mfa-error-user-not-initiated');
        case AuthgardAdminErrorCode.INVALID_OPERATION:
            return t('mfa-error-invalid-operation');
        case AuthgardAdminErrorCode.USER_ONBOARDED:
            return t('mfa-error-user-onboarded');
        case AuthgardAdminErrorCode.USER_NOT_ONBOARDED:
            return t('mfa-error-user-not-onboarded');
        case AuthgardAdminErrorCode.REREGISTER_INITIATED:
            return t('mfa-error-reregister-initiated');
        case AuthgardAdminErrorCode.METHOD_REQUIRED:
            return t('mfa-error-method-required');
        case AuthgardAdminErrorCode.METHOD_NOT_PERMITTED:
            return t('mfa-error-method-not-permitted');
        case AuthgardAdminErrorCode.ACTIVE_REGCODE_EXISTS:
            return t('mfa-error-active-regcode-exists');
        case AuthgardAdminErrorCode.USER_1P_NOT_ALLOWED:
            return t('mfa-error-user-1p-not-allowed');
        case AuthgardAdminErrorCode.DUPLICATE_USER:
            return t('mfa-error-duplicate-user');
        case AuthgardAdminErrorCode.USER_MFA_INVITE_EXPIRED:
            return t('mfa-error-user-mfa-invite-expired');
        case AuthgardAdminErrorCode.NOT_AUTHORIZED:
            return t('mfa-error-not-authorized');
        case AuthgardAdminErrorCode.PRINCIPAL_NOT_FOUND:
            return t('mfa-error-principal-not-found');
        default:
            return code
                ? t('mfa-error-generic-with-code', { code })
                : t('mfa-error-generic');
    }
};

/**
 * Normalizes a raw mfaStatus string (from bulkGetMfaStatus) to the {@link AuthgardMfaStatus} enum.
 * Unknown or absent values fall back to NO_RECORD so a status the backend adds later degrades
 * safely rather than throwing.
 *
 * @param raw the raw mfaStatus string off the wire.
 * @return the matching {@link AuthgardMfaStatus}, or NO_RECORD if unrecognized.
 */
export const normalizeMfaStatus = (raw?: string): AuthgardMfaStatus => {
    switch (raw) {
        case AuthgardMfaStatus.INITIATED:
            return AuthgardMfaStatus.INITIATED;
        case AuthgardMfaStatus.REINITIATED:
            return AuthgardMfaStatus.REINITIATED;
        case AuthgardMfaStatus.ONBOARDED:
            return AuthgardMfaStatus.ONBOARDED;
        case AuthgardMfaStatus.RESET_FIDO2:
            return AuthgardMfaStatus.RESET_FIDO2;
        case AuthgardMfaStatus.REREGISTER:
            return AuthgardMfaStatus.REREGISTER;
        case AuthgardMfaStatus.NO_RECORD:
        default:
            return AuthgardMfaStatus.NO_RECORD;
    }
};

/**
 * Maps a principal's {@link MfaProvider} to the Authgard credential type string used by
 * bulkGetMfaStatus (credentialType). Returns undefined for non-Authgard providers (e.g. MILO),
 * which have no Authgard credential to look up.
 */
const credentialTypeForProvider = (provider?: string): string | undefined => {
    switch (provider) {
        case MfaProvider.AUTHGARD_SCAN2AUTH:
            return 'SCAN2AUTH';
        case MfaProvider.AUTHGARD_FIDO2:
            return 'FIDO2';
        default:
            return undefined;
    }
};

/**
 * Resolves the bulkGetMfaStatus credentials list for a principal down to the single
 * {status, expiry} the card/modal consumes, by picking the credential whose type matches the
 * principal's MFA provider. The invite expiry is passed through verbatim as the backend's raw
 * ISO-8601 string (the modal parses it to a Date); expired-ness is derived from it downstream, not
 * computed here, so there is a single source of truth for the timestamp. Returns {NO_RECORD, null}
 * when there is no matching credential (a failure entry, an empty list, an unknown provider, or the
 * principal simply having no record).
 *
 * @param credentials the principal's credentials from bulkGetMfaStatus.
 * @param provider the principal's MFA provider (drives which credential to read).
 * @return the {status, expiry} for the card (expiry is the raw ISO string, or null).
 */
export const mfaStatusForProvider = (
    credentials: { credentialType: string; mfaStatus: string; mfaInviteExpiry?: string | null }[],
    provider?: string,
): { status: AuthgardMfaStatus; expiry: string | null } => {
    const credentialType = credentialTypeForProvider(provider);
    const credential = credentialType
        ? credentials.find(c => c.credentialType === credentialType)
        : undefined;

    if (!credential) {
        return { status: AuthgardMfaStatus.NO_RECORD, expiry: null };
    }

    return {
        status: normalizeMfaStatus(credential.mfaStatus),
        expiry: credential.mfaInviteExpiry ?? null,
    };
};

/**
 * Maps a raw idpType string (from a getScopeMappings scope mapping) to the {@link IdpType} enum.
 *
 * Throws on an unrecognized value — including one the backend adds later — so the caller surfaces an
 * error rather than silently showing the wrong MFA controls for an IdP type the UI doesn't support.
 *
 * @param raw the raw idpType string off the wire.
 * @return the matching {@link IdpType}.
 * @throws if `raw` is not a recognized idpType.
 */
export const normalizeIdpType = (raw?: string): IdpType => {
    switch (raw) {
        case IdpType.AUTHGARD:
            return IdpType.AUTHGARD;
        case IdpType.MILO:
            return IdpType.MILO;
        case IdpType.DEFAULT:
            return IdpType.DEFAULT;
        default:
            throw new Error(`Unrecognized idpType: ${raw}`);
    }
};

/**
 * Maps a scope's configured {@link IdpType} (from getScopeMappings) plus a principal's
 * {@link UserType} to the MFA providers whose management options should be surfaced for that
 * principal. This is the single source of truth for the "which MFA controls do we show" decision,
 * shared by principal-list.tsx and the bulk/per-principal action components.
 *
 * - AUTHGARD sites split by user type: associates manage Scan2Auth, admins manage FIDO2. This
 *   mirrors the Authgard model where associates enroll via Scan2Auth and admins via FIDO2.
 * - MILO and DEFAULT fall back to the existing MILO Register-MFA flow. (An unrecognized idpType
 *   never reaches here — normalizeIdpType throws on it upstream.)
 *
 * This is a presentation decision only. Every admin operation is authorized server-side by PCS /
 * Authgard, so surfacing the wrong option never grants an operation the caller isn't entitled to —
 * the backend still rejects it.
 *
 * @param idpType the scope's configured identity provider.
 * @param userType the principal's user type.
 * @return the MFA providers to drive the action menus for this principal.
 */
export const mfaProvidersForIdpType = (idpType: IdpType, userType: string): MfaProvider[] => {
    if (idpType === IdpType.AUTHGARD) {
        const isAdmin = userType !== UserType.ASSOCIATE;
        return [isAdmin ? MfaProvider.AUTHGARD_FIDO2 : MfaProvider.AUTHGARD_SCAN2AUTH];
    }
    // MILO and DEFAULT both use the existing MILO Register-MFA flow.
    return [MfaProvider.MILO];
};

/**
 * Converts the {@link Date} object to a {@link String} using the PreStart Hire API format of 'yyyy-MM-dd'.
 *
 * @param date the date to convert.
 * @return the formatted date.
 */
export const convertDateToPreStartFormat = (date: Date) => {
    let day, month, year;

    day = date.getUTCDate();
    month = date.getUTCMonth() + 1;
    year = date.getUTCFullYear();

    day = day.toString().padStart(2, '0');
    month = month.toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * Checks if the Email is valid.
 *
 * @param email The value to validate.
 * @return true if valid; otherwise, false.
 */
export const isEmailValid = (email?: string): boolean => {
    if (!email) {
        return false;
    }

    // https://stackoverflow.com/questions/46155/how-can-i-validate-an-email-address-in-javascript/9204568#9204568
    return /^[^\s@]+?@[^\s@]+?\.[^\s@]+$/.test(email);
}

export const generateBaseliningPrincipalWorkflowArn = (siteId: string): string => {
    return `arn:aftx:${REGION}:${TenantID.AFTX}:${ENTERPRISE_SCOPE}:site/${siteId}`
}

export const generateBaseliningPermissionWorkflowArn = (siteId: string, permissionId: string): string => {
    return `arn:sox:${REGION}:${TenantID.SOX}:${permissionId}:site/${siteId}`
}

/**
 * Gets the translated i18n error message based on the data in the ValidationError object.
 *
 * @param t The i18n translation function.
 * @param error The validation error containing translationKey and placeholderValues.
 */
export const getTranslatedErrorMsg = (t: TFunction, error: ValidationError): string => {
    if (error.placeholderValues) {
        const msg = t(error.translationKey);

        // Extract all values inside curly braces into list
        //
        // Example:
        // msg = "User with First Name of {firstName} and Last Name of {lastName} for Warehouse {warehouse} is a duplicate."
        // matches = ["firstName", "lastName", "warehouse"]
        const matches = msg.match(/[^{}]+(?=})/g);

        // Check if message contains placeholder and that number of placeholders match number of placeholder values
        // Note: This is just a sanity check that should always be true
        if (matches && (matches.length == error.placeholderValues.length)) {
            // Dynamically create object to use for i18n translation function below
            //
            // Example:
            // matches = ["firstName", "lastName", "warehouse"]
            // error.placeholderValues = ["Jeff", "Bezos", "BFI1"]
            // obj = {
            //     "firstName": "'Jeff'",
            //     "lastName": "'Bezos'",
            //     "warehouse: "'BFI1'"
            // }
            let obj: { [key: string]: string } = {};
            for (let i = 0; i < error.placeholderValues.length; i++) {
                obj[matches[i]] = `'${error.placeholderValues[i]}'`;
            }

            return t(error.translationKey, obj);
        }

        // Mismatch between the placeholders in the translated string and the
        // values supplied by the backend. Falling through would render the raw
        // "{placeholder}" tokens to the user, so log it for observability.
        logger.warn("ValidationError placeholder count does not match the translated string", {
            translationKey: error.translationKey,
            placeholders: matches,
            placeholderValues: error.placeholderValues,
        });
    }

    return t(error.translationKey);
}

/**
 * Signs out the current user.
 */
export const signOut = () => {
    logger.info("Signing out user from FMUI and redirecting to AFT-X Menu logout page.");

    // Clear FIT tokens (access_token, id_token, etc.) from session storage
    sessionStorage.clear();

    // Redirect to AFT-X Menu logout page
    window.location.assign(`${AFTX_MENU_ENDPOINT}/menu/logout`);
}

/**
 * Checks if the system is currently in dark mode.
 * @returns boolean indicating if dark mode is active
 */
export const isDarkMode = (): boolean => {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
};

/**
 * Sets up a dark mode listener and returns a cleanup function.
 * @param callback Function to be called when dark mode changes
 * @returns Function to remove the listener
 */
export const addDarkModeListener = (callback: (isDark: boolean) => void): (() => void) => {
    const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleThemeChange = (e: MediaQueryListEvent) => {
        callback(e.matches);
    };

    darkModeMediaQuery.addEventListener('change', handleThemeChange);

    return () => {
        darkModeMediaQuery.removeEventListener('change', handleThemeChange);
    };
};

/**
 * Determines the realm that the website is hosted in based on the region.
 * @returns the realm the website is hosted in
 */
export const getRealm = () => {
    const region = "us-west-2".toLowerCase();  // Filled from Webpack
    if (region === 'us-west-2' || region === 'ap-northeast-1') {
        // MFA/MILO integration is not setup in ap-northeast-1, so us-west-2 is used for JP
        return "JPAmazon";
    } else if(region === 'eu-west-1') {
        return  "EUAmazon";
    } else {
        return "USAmazon";
    }
}
